import { useState, useEffect } from "react";
import api from "../services/api";
import { PLAN_FEATURES } from "../config/planFeatures";

const MODULE_LABELS = {
  crm:         "CRM & Prospection",
  planning:    "Planning & RDV",
  abonnements: "Abonnements récurrents",
  stocks:      "Stocks & Commandes",
  ged:         "Espace documentaire (GED)",
  analytique:  "Comptabilité analytique",
  budget:      "Budget & Prévisionnel",
  conformite:  "Conformité & documents export",
  groupe:      "Vue Groupe & Consolidation",
};

// Prix annuel affiché « par mois » (plans.js, via /billing/plans) → total
// réellement facturé une fois par an.
const annualTotal = perMonth => (perMonth * 12).toLocaleString('fr-FR').replace(/ /g, ' ');

const S = {
  page:    { minHeight:"100vh", background:"var(--bg-primary)", color:"var(--text-primary)", padding:24, fontFamily:"inherit" },
  title:   { fontSize:18, fontWeight:500, color:"var(--text-primary)", margin:"0 0 20px" },
  card:    { background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 18px" },
  label:   { fontSize:10, fontWeight:500, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6, display:"block" },
  toggle:  { display:"flex", background:"var(--bg-card)", borderRadius:8, padding:3, gap:3, width:"fit-content", marginBottom:20 },
  tBtnOn:  { fontSize:12, padding:"6px 16px", borderRadius:6, border:"none", cursor:"pointer", fontWeight:600, background:"var(--accent)", color:"#fff" },
  tBtnOff: { fontSize:12, padding:"6px 16px", borderRadius:6, border:"none", cursor:"pointer", fontWeight:600, background:"transparent", color:"var(--text-muted)" },
  plansGrid: { display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:14, marginBottom:20 },
  planCard:  { background:"var(--bg-card)", borderRadius:12, padding:20, display:"flex", flexDirection:"column", position:"relative" },
  btn:     { padding:"9px 0", width:"100%", borderRadius:8, border:"none", cursor:"pointer", fontSize:13, fontWeight:700, marginTop:14 },
  pill:    { display:"inline-block", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:999, background:"rgba(26,158,114,0.12)", color:"var(--accent)", border:"1px solid rgba(26,158,114,0.25)" },
};

export default function MonAbonnement() {
  const [plan, setPlan]           = useState(null);
  const [catalog, setCatalog]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [cycle, setCycle]         = useState("annual");
  const [satelliteInput, setSatelliteInput] = useState(0);
  const [msg, setMsg]             = useState("");
  const [busyKey, setBusyKey]     = useState(null); // plan_key en cours de traitement

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([api.billing.myPlan(), api.billing.getPlans()])
      .then(([planData, catalogData]) => {
        setPlan(planData);
        setCatalog(catalogData);
        setCycle(planData.billing_cycle || "annual");
        setSatelliteInput(planData.satellite_addon_quantity || 0);
      })
      .catch(() => setMsg("Erreur de chargement"))
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const [confirming, setConfirming] = useState(false);
  const confirmRenewal = async () => {
    setConfirming(true);
    try {
      await api.billing.confirmRenewal();
      notify("Renouvellement confirmé — merci !");
      load(true);
    } catch (err) { notify(err.message || "Erreur lors de la confirmation."); }
    finally { setConfirming(false); }
  };

  const choosePlan = async (planKey) => {
    const includesSatellite = catalog?.plans.find(p => p.key === planKey)?.includesSatellite;
    const satelliteCount = includesSatellite ? Math.max(0, Math.min(50, parseInt(satelliteInput, 10) || 0)) : 0;
    setBusyKey(planKey);
    try {
      if (plan?.has_active_subscription) {
        const res = await api.billing.changePlan({ plan_key: planKey, billing_cycle: cycle, satellite_count: satelliteCount });
        notify(`Forfait ${res.plan_key} activé.`);
        load(true);
      } else {
        const res = await api.billing.createCheckoutSession({ plan_key: planKey, billing_cycle: cycle, satellite_count: satelliteCount });
        window.location.href = res.redirect_url;
      }
    } catch (err) {
      notify(err.message || "Erreur lors du changement de forfait.");
    } finally { setBusyKey(null); }
  };

  if (loading) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <p style={{ color:"var(--text-muted)" }}>Chargement…</p>
    </div>
  );

  const billingFrozen = plan?.billing_enabled === false;

  return (
    <div style={S.page}>
      <h1 style={S.title}>Mon abonnement</h1>

      {msg && (
        <div style={{ marginBottom:12, padding:"8px 14px", borderRadius:8,
          background: msg.includes("Erreur") ? "rgba(239,68,68,0.1)" : "rgba(26,158,114,0.1)",
          color: msg.includes("Erreur") ? "#ef4444" : "var(--accent)",
          border: `1px solid ${msg.includes("Erreur") ? "rgba(239,68,68,0.3)" : "rgba(26,158,114,0.3)"}`,
          fontSize:13 }}>
          {msg}
        </div>
      )}

      {/* Client pilote (29/10/2026) : remplace l'encadré d'essai */}
      {plan?.pilot && !plan?.has_active_subscription && (() => {
        const until = plan.pilot.until.split("-").reverse().join("/");
        const daysLeft = Math.ceil((new Date(`${plan.pilot.until}T23:59:59`) - new Date()) / 86400000);
        const ended = !plan.pilot.active;
        const color = ended ? "#ef4444" : daysLeft <= 15 ? "#f59e0b" : "var(--accent)";
        return (
          <div style={{ marginBottom:16, padding:"14px 18px", borderRadius:10, background: ended ? "rgba(239,68,68,0.12)" : "rgba(26,158,114,0.1)", border:`1px solid ${ended ? "rgba(239,68,68,0.35)" : "rgba(26,158,114,0.3)"}` }}>
            <div style={{ fontSize:14, fontWeight:600, color }}>
              {ended ? `Votre période de client pilote s'est terminée le ${until}` : `Client pilote — sans facturation jusqu'au ${until}`}
            </div>
            <div style={{ fontSize:12, color:"var(--text-muted)", marginTop:2 }}>
              {ended
                ? "Vos données restent consultables et exportables (FEC, grand livre, balance, synthèse TVA). Pour continuer à les modifier, contactez ExecPro360 (support@execpro360.com)."
                : "Ensuite, vos données resteront consultables et exportables ; leur modification demandera un abonnement. Vous serez prévenu 15 jours et 3 jours avant."}
            </div>
          </div>
        );
      })()}

      {plan?.status === "trial" && plan?.trial_end_date && !plan?.pilot && (() => {
        const daysLeft = Math.ceil((new Date(plan.trial_end_date) - new Date()) / 86400000);
        const expired  = daysLeft <= 0;
        const urgent   = daysLeft > 0 && daysLeft <= 3;
        const bg     = expired ? "rgba(239,68,68,0.12)" : urgent ? "rgba(245,158,11,0.12)" : "rgba(26,158,114,0.1)";
        const border = expired ? "rgba(239,68,68,0.35)" : urgent ? "rgba(245,158,11,0.35)" : "rgba(26,158,114,0.3)";
        const color  = expired ? "#ef4444" : urgent ? "#f59e0b" : "var(--accent)";
        return (
          <div style={{ marginBottom:16, padding:"14px 18px", borderRadius:10, background:bg, border:`1px solid ${border}` }}>
            <div style={{ fontSize:14, fontWeight:600, color }}>
              {expired ? "Votre essai gratuit est terminé" : `Il vous reste ${daysLeft} jour${daysLeft > 1 ? "s" : ""} d'essai gratuit`}
            </div>
            <div style={{ fontSize:12, color:"var(--text-muted)", marginTop:2 }}>
              {expired ? "Choisissez un forfait ci-dessous pour continuer à utiliser ExecPro360 sans interruption." : "Choisissez votre forfait dès maintenant pour profiter de l'offre de lancement."}
            </div>
          </div>
        );
      })()}

      {plan?.renewal?.current_period_end && !plan.renewal.confirmed && (() => {
        const r = plan.renewal;
        const daysLeft = Math.ceil((new Date(r.current_period_end) - new Date()) / 86400000);
        const hard    = r.renewal_hard_blocked;
        const blocked = r.renewal_blocked;
        const bg     = hard ? "rgba(239,68,68,0.14)" : blocked ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)";
        const border = hard ? "rgba(239,68,68,0.45)" : blocked ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.35)";
        const color  = hard || blocked ? "#ef4444" : "#f59e0b";
        const title  = hard ? "Accès bloqué — renouvellement non confirmé" : blocked ? "Accès en lecture seule — renouvellement non confirmé" : `Renouvellement à confirmer${daysLeft > 0 ? ` dans ${daysLeft} jour${daysLeft > 1 ? "s" : ""}` : ""}`;
        const sub = hard ? `La période de grâce est terminée (depuis le ${r.renewal_grace_ends_at}). Confirmez pour rétablir l'accès en écriture.` : blocked ? `Confirmez avant le ${r.renewal_grace_ends_at} pour éviter un blocage complet. Vos données restent consultables et exportables.` : `Échéance le ${r.current_period_end} — confirmez pour continuer sans interruption.`;
        return (
          <div style={{ marginBottom:16, padding:"14px 18px", borderRadius:10, background:bg, border:`1px solid ${border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
            <div>
              <div style={{ fontSize:14, fontWeight:600, color }}>{title}</div>
              <div style={{ fontSize:12, color:"var(--text-muted)", marginTop:2 }}>{sub}</div>
            </div>
            <button style={{ padding:"8px 16px", borderRadius:8, border:"none", cursor:"pointer", fontSize:12, fontWeight:700, background:color, color:"#fff", flexShrink:0 }}
              disabled={confirming} onClick={confirmRenewal}>
              {confirming ? "Confirmation…" : "Je confirme le renouvellement"}
            </button>
          </div>
        );
      })()}

      {billingFrozen && (
        <div style={{ marginBottom:16, padding:"14px 18px", borderRadius:10, background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.3)", fontSize:13, color:"#f59e0b", fontWeight:600 }}>
          Les abonnements payants ouvrent prochainement — votre essai gratuit reste utilisable normalement en attendant.
        </div>
      )}

      {/* Forfait actuel */}
      {plan?.has_active_subscription && (
        <div style={{ ...S.card, marginBottom:20, border:"1px solid rgba(26,158,114,0.35)" }}>
          <span style={S.label}>Forfait actuel</span>
          <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:8 }}>
            <span style={{ fontSize:22, fontWeight:700, color:"var(--accent)" }}>{plan.plan_name}</span>
            <span style={{ fontSize:14, color:"var(--text-muted)" }}>{plan.totalMonthly} € HT/mois {plan.billing_cycle === "annual" ? `(soit ${annualTotal(plan.totalMonthly)} € HT facturés annuellement, engagement 12 mois)` : "(facturé mensuellement, sans engagement, résiliable à tout moment)"}</span>
          </div>
          {plan.satellite_addon_quantity > 0 && (
            <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:8 }}>+ {plan.satellite_addon_quantity} société{plan.satellite_addon_quantity > 1 ? "s" : ""} satellite{plan.satellite_addon_quantity > 1 ? "s" : ""} additionnelle{plan.satellite_addon_quantity > 1 ? "s" : ""}</div>
          )}
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {plan.modules.length === 0 ? (
              <span style={{ fontSize:11, color:"var(--text-muted)" }}>Forfait Essentiel — {PLAN_FEATURES.essentiel.features.join(" · ")}</span>
            ) : plan.modules.map(k => (
              <span key={k} style={S.pill}>{MODULE_LABELS[k] || k}</span>
            ))}
          </div>
        </div>
      )}

      {/* Toggle mensuel / annuel */}
      <div style={S.toggle}>
        <button style={cycle === "monthly" ? S.tBtnOn : S.tBtnOff} onClick={() => setCycle("monthly")}>Mensuel</button>
        <button style={cycle === "annual" ? S.tBtnOn : S.tBtnOff} onClick={() => setCycle("annual")}>Annuel</button>
      </div>

      {/* Les 3 forfaits */}
      {catalog && (
        <div style={S.plansGrid}>
          {catalog.plans.map(p => {
            const isCurrent = plan?.plan_key === p.key && plan?.billing_cycle === cycle;
            const price = cycle === "annual" ? p.priceAnnual : p.priceMonthly;
            const recommended = p.key === "business";
            return (
              <div key={p.key} style={{ ...S.planCard, border: recommended ? "2px solid var(--accent)" : "1px solid var(--border)" }}>
                {recommended && (
                  <span style={{ position:"absolute", top:-11, left:20, background:"var(--accent)", color:"#fff", fontSize:10, fontWeight:800, padding:"3px 10px", borderRadius:20 }}>RECOMMANDÉ</span>
                )}
                <div style={{ fontSize:15, fontWeight:700, color:"var(--text-primary)", marginBottom:6 }}>{p.name}</div>
                <div style={{ fontSize:26, fontWeight:800, color:"var(--text-primary)" }}>{price}&nbsp;€<span style={{ fontSize:12, fontWeight:500, color:"var(--text-muted)" }}> HT/mois</span></div>
                <div style={{ fontSize:11, color:"var(--text-secondary)", marginBottom:2 }}>
                  {cycle === "annual" ? <>soit {annualTotal(p.priceAnnual)}&nbsp;€ HT facturés annuellement · engagement 12&nbsp;mois</> : "facturé mensuellement · sans engagement, résiliable à tout moment"}
                </div>
                <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:10 }}>{p.maxUsers === 1 ? "1 utilisateur" : `Jusqu'à ${p.maxUsers} utilisateurs`}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:4, flexGrow:1 }}>
                  {PLAN_FEATURES[p.key]?.inherits && (
                    <span style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)", marginBottom:2 }}>{PLAN_FEATURES[p.key].inherits}</span>
                  )}
                  {(PLAN_FEATURES[p.key]?.features || p.modules.map(k => MODULE_LABELS[k] || k)).map(label => (
                    <span key={label} style={{ fontSize:11, color:"var(--text-muted)" }}>✓ {label}</span>
                  ))}
                </div>
                {p.includesSatellite && catalog.satelliteAddon && (
                  <div style={{ marginTop:12, padding:"8px 10px", borderRadius:8, background:"var(--bg-primary)", border:"1px solid var(--border)" }}>
                    <div style={{ fontSize:10.5, color:"var(--text-muted)", marginBottom:4 }}>Sociétés satellites additionnelles ({cycle === "annual" ? `${catalog.satelliteAddon.priceAnnual} € HT/mois chacune, soit ${annualTotal(catalog.satelliteAddon.priceAnnual)} € HT facturés annuellement` : `${catalog.satelliteAddon.priceMonthly} € HT/mois chacune`}, 1 déjà incluse)</div>
                    <input type="number" min={0} max={50}
                      value={satelliteInput}
                      onChange={e => setSatelliteInput(Math.max(0, Math.min(50, parseInt(e.target.value, 10) || 0)))}
                      style={{ width:60, padding:"4px 8px", background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:6, color:"var(--text-primary)", fontSize:13, textAlign:"center" }}
                    />
                  </div>
                )}
                <button
                  style={{ ...S.btn, background: isCurrent ? "var(--bg-primary)" : "var(--accent)", color: isCurrent ? "var(--text-muted)" : "#fff", cursor: isCurrent || billingFrozen ? "not-allowed" : "pointer", opacity: busyKey === p.key ? 0.6 : 1 }}
                  disabled={isCurrent || billingFrozen || busyKey === p.key}
                  onClick={() => choosePlan(p.key)}
                >
                  {isCurrent ? "Forfait actuel" : busyKey === p.key ? "Traitement…" : plan?.has_active_subscription ? "Changer pour ce forfait" : "Choisir ce forfait"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {catalog?.satelliteAddon && (
        <div style={{ textAlign:"center", fontSize:11.5, color:"var(--text-muted)" }}>
          L'add-on société satellite n'est disponible qu'en complément du forfait Ultimate.
        </div>
      )}
    </div>
  );
}
